import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line,
} from "recharts";
import {
  getAnalyticsOverview,
  getAnalyticsChannels,
  getAnalyticsConversions,
  getAnalyticsDevicesGeo,
  getAnalyticsPages,
  getAnalyticsEngagement,
  getAnalyticsFunnels,
  getAnalyticsRetention,
  getAnalyticsRealtime,
  type KpiDelta,
} from "@/lib/analytics/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  head: () => ({
    meta: [
      { title: "Analytics — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminAnalytics,
});

const RANGES = [
  { d: 1, label: "Idag" },
  { d: 7, label: "7d" },
  { d: 30, label: "30d" },
  { d: 90, label: "90d" },
  { d: 365, label: "12m" },
];

const TABS = [
  { id: "overview", label: "Översikt" },
  { id: "realtime", label: "Realtid" },
  { id: "channels", label: "Kanaler" },
  { id: "pages", label: "Sidor" },
  { id: "engagement", label: "Engagemang" },
  { id: "funnels", label: "Funnels" },
  { id: "retention", label: "Retention" },
  { id: "conversions", label: "Konverteringar" },
  { id: "devices", label: "Enheter & geo" },
] as const;
type TabId = (typeof TABS)[number]["id"];

// ---------- utils ----------
const nf = new Intl.NumberFormat("sv-SE");
const fmt = (n: number) => nf.format(Math.round(n));
const fmtPct = (n: number, digits = 1) => `${(n * 100).toFixed(digits)}%`;
const fmtDur = (s: number) => {
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60); const r = Math.round(s % 60);
  return `${m}m ${r}s`;
};
function csv(rows: (string | number | null | undefined)[][]): string {
  return rows.map((r) =>
    r.map((c) => {
      const v = c == null ? "" : String(c);
      return /[",\n;]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(";"),
  ).join("\n");
}
function download(name: string, body: string, mime = "text/csv") {
  const blob = new Blob([body], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

// ---------- primitives ----------
function Kpi({ label, k, format = fmt, suffix }: { label: string; k: KpiDelta; format?: (n: number) => string; suffix?: string }) {
  const up = (k.delta_pct ?? 0) >= 0;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-xs text-white/60">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-white">{format(k.value)}{suffix ?? ""}</div>
      <div className="mt-1 text-xs">
        {k.delta_pct === null ? (
          <span className="text-white/40">— jmf föreg.</span>
        ) : (
          <span className={up ? "text-mint" : "text-red-400"}>
            {up ? "▲" : "▼"} {Math.abs(k.delta_pct).toFixed(1)}% vs föreg.
          </span>
        )}
      </div>
    </div>
  );
}

function Section({ title, action, children }: { title?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between gap-3">
          {title && <h2 className="text-sm font-medium text-white/80">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function Table({ headers, rows, sticky = false }: { headers: string[]; rows: (string | number)[][]; sticky?: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className={sticky ? "sticky top-0 bg-[#0b0b10]" : ""}>
          <tr className="border-b border-white/10 text-left text-xs uppercase text-white/50">
            {headers.map((h) => (<th key={h} className="py-2 pr-4 font-medium">{h}</th>))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={headers.length} className="py-6 text-center text-white/40">Ingen data ännu</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
              {r.map((c, j) => (<td key={j} className="py-2 pr-4 text-white/85">{c}</td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExportBtn({ name, rows }: { name: string; rows: (string | number | null | undefined)[][] }) {
  return (
    <button
      onClick={() => download(`${name}-${new Date().toISOString().slice(0, 10)}.csv`, csv(rows))}
      className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/70 hover:bg-white/5"
    >Export CSV</button>
  );
}

// ---------- charts ----------
const CHART_AXIS = { stroke: "rgba(255,255,255,0.4)", fontSize: 11 };

function DailyArea({ data }: { data: { date: string; sessions: number; visitors: number; pageviews: number }[] }) {
  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--mint, 160 60% 55%))" stopOpacity={0.4} />
              <stop offset="100%" stopColor="hsl(var(--mint, 160 60% 55%))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="date" tick={CHART_AXIS} tickFormatter={(d) => d.slice(5)} />
          <YAxis tick={CHART_AXIS} width={40} />
          <Tooltip contentStyle={{ background: "#0b0b10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
          <Area type="monotone" dataKey="sessions" stroke="#5eead4" fill="url(#g1)" strokeWidth={2} name="Sessioner" />
          <Area type="monotone" dataKey="visitors" stroke="#a78bfa" fill="transparent" strokeWidth={1.5} name="Besökare" />
          <Area type="monotone" dataKey="pageviews" stroke="#f59e0b" fill="transparent" strokeWidth={1.5} name="Sidvisningar" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function SimpleBar({ data, x, y, name }: { data: any[]; x: string; y: string; name: string }) {
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey={x} tick={CHART_AXIS} />
          <YAxis tick={CHART_AXIS} width={40} />
          <Tooltip contentStyle={{ background: "#0b0b10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
          <Bar dataKey={y} fill="#5eead4" radius={[4, 4, 0, 0]} name={name} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SimpleLine({ data, x, y, name }: { data: any[]; x: string; y: string; name: string }) {
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey={x} tick={CHART_AXIS} tickFormatter={(d) => String(d).slice(5)} />
          <YAxis tick={CHART_AXIS} width={40} />
          <Tooltip contentStyle={{ background: "#0b0b10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
          <Line type="monotone" dataKey={y} stroke="#5eead4" strokeWidth={2} dot={false} name={name} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- page ----------
function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState<TabId>("overview");

  const overviewFn = useServerFn(getAnalyticsOverview);
  const channelsFn = useServerFn(getAnalyticsChannels);
  const conversionsFn = useServerFn(getAnalyticsConversions);
  const devicesFn = useServerFn(getAnalyticsDevicesGeo);
  const pagesFn = useServerFn(getAnalyticsPages);
  const engagementFn = useServerFn(getAnalyticsEngagement);
  const funnelsFn = useServerFn(getAnalyticsFunnels);
  const retentionFn = useServerFn(getAnalyticsRetention);
  const realtimeFn = useServerFn(getAnalyticsRealtime);

  const overview = useQuery({ queryKey: ["a", "overview", days], queryFn: () => overviewFn({ data: { days } }), enabled: tab === "overview" });
  const channels = useQuery({ queryKey: ["a", "channels", days], queryFn: () => channelsFn({ data: { days } }), enabled: tab === "channels" });
  const conversions = useQuery({ queryKey: ["a", "conv", days], queryFn: () => conversionsFn({ data: { days } }), enabled: tab === "conversions" });
  const devices = useQuery({ queryKey: ["a", "dev", days], queryFn: () => devicesFn({ data: { days } }), enabled: tab === "devices" });
  const pages = useQuery({ queryKey: ["a", "pages", days], queryFn: () => pagesFn({ data: { days } }), enabled: tab === "pages" });
  const engagement = useQuery({ queryKey: ["a", "eng", days], queryFn: () => engagementFn({ data: { days } }), enabled: tab === "engagement" });
  const funnels = useQuery({ queryKey: ["a", "fun", days], queryFn: () => funnelsFn({ data: { days } }), enabled: tab === "funnels" });
  const retention = useQuery({ queryKey: ["a", "ret", days], queryFn: () => retentionFn({ data: { days } }), enabled: tab === "retention" });
  const realtime = useQuery({
    queryKey: ["a", "rt"], queryFn: () => realtimeFn(), enabled: tab === "realtime",
    refetchInterval: tab === "realtime" ? 10_000 : false,
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Analytics</h1>
          <p className="mt-1 text-sm text-white/60">First-party insikter — trafik, kanaler, engagemang, funnels &amp; konverteringar.</p>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.d}
              onClick={() => setDays(r.d)}
              className={`rounded-full px-3 py-1.5 text-xs ${days === r.d ? "bg-mint text-mint-foreground" : "border border-white/15 text-white/70 hover:bg-white/5"}`}
            >{r.label}</button>
          ))}
          <a href="/admin/leads" className="ml-2 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5">Leads →</a>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-1 border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === t.id ? "border-mint text-white" : "border-transparent text-white/50 hover:text-white"}`}
          >{t.label}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <Pane q={overview}>
          {overview.data && (
            <>
              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                <Kpi label="Besökare" k={overview.data.visitors} />
                <Kpi label="Sessioner" k={overview.data.sessions} />
                <Kpi label="Sidvisningar" k={overview.data.pageviews} />
                <Kpi label="Leads" k={overview.data.leads} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                <Kpi label="Konv.rate" k={overview.data.conv_rate} format={(n) => fmtPct(n)} />
                <Kpi label="Bounce-rate" k={overview.data.bounce_rate} format={(n) => fmtPct(n)} />
                <Kpi label="Engaged-rate" k={overview.data.engaged_rate} format={(n) => fmtPct(n)} />
                <Kpi label="Snitt sessionstid" k={overview.data.avg_session_sec} format={(n) => fmtDur(n)} />
              </div>
              <Section title="Trafik per dag" action={
                <ExportBtn name="trafik-daily"
                  rows={[["Datum", "Sessioner", "Besökare", "Sidvisningar", "Leads", "Events"], ...overview.data.daily.map((d) => [d.date, d.sessions, d.visitors, d.pageviews, d.leads, d.events])]} />
              }>
                <DailyArea data={overview.data.daily} />
              </Section>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <Section title="Topp-sidor">
                  <Table headers={["Sida", "Visningar"]} rows={overview.data.top_pages.map((p) => [p.path, fmt(p.views)])} />
                </Section>
                <Section title="Topp-källor (referrer)">
                  <Table headers={["Källa", "Sessioner"]} rows={overview.data.top_referrers.map((p) => [p.host, fmt(p.sessions)])} />
                </Section>
              </div>
            </>
          )}
        </Pane>
      )}

      {/* REALTIME */}
      {tab === "realtime" && (
        <Pane q={realtime}>
          {realtime.data && (
            <>
              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3">
                <Kpi label="Aktiva besökare (5 min)" k={{ value: realtime.data.active_visitors, prev: 0, delta_pct: null }} />
                <Kpi label="Aktiva sessioner" k={{ value: realtime.data.active_sessions, prev: 0, delta_pct: null }} />
                <div className="rounded-xl border border-mint/30 bg-mint/5 p-4">
                  <div className="text-xs text-mint">Live</div>
                  <div className="mt-1 text-2xl font-semibold text-white">●</div>
                  <div className="mt-1 text-xs text-white/60">Uppdateras var 10:e sekund</div>
                </div>
              </div>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <Section title="Aktiva sidor just nu">
                  <Table headers={["Sida", "Visningar"]} rows={realtime.data.active_paths.map((p) => [p.path, fmt(p.count)])} />
                </Section>
                <Section title="Senaste events">
                  <Table
                    headers={["Tid", "Event", "Sida", "Etikett"]}
                    rows={realtime.data.recent_events.map((e) => [
                      new Date(e.ts).toLocaleTimeString("sv-SE"),
                      e.name,
                      e.path,
                      e.label ?? "—",
                    ])}
                  />
                </Section>
              </div>
            </>
          )}
        </Pane>
      )}

      {/* CHANNELS */}
      {tab === "channels" && (
        <Pane q={channels}>
          {channels.data && (
            <Section title={`${channels.data.length} kanalrader`} action={
              <ExportBtn name="kanaler" rows={[
                ["Källa", "Medium", "Kampanj", "Grupp", "Sessioner", "Besökare", "Leads", "Konv.rate"],
                ...channels.data.map((r) => [r.source, r.medium, r.campaign ?? "", r.group, r.sessions, r.visitors, r.leads, (r.conv_rate * 100).toFixed(2) + "%"]),
              ]} />
            }>
              <Table
                headers={["Källa", "Medium", "Kampanj", "Grupp", "Sessioner", "Besökare", "Leads", "Konv.rate"]}
                rows={channels.data.map((r) => [r.source, r.medium, r.campaign ?? "—", r.group, fmt(r.sessions), fmt(r.visitors), fmt(r.leads), fmtPct(r.conv_rate)])}
              />
            </Section>
          )}
        </Pane>
      )}

      {/* PAGES */}
      {tab === "pages" && (
        <Pane q={pages}>
          {pages.data && (
            <Section title={`${pages.data.length} sidor`} action={
              <ExportBtn name="sidor" rows={[
                ["Sida", "Visningar", "Unika besökare", "Snitt tid (s)", "Snitt scroll %", "Bounce-rate", "Entries", "Exits"],
                ...pages.data.map((p) => [p.path, p.views, p.unique_visitors, p.avg_time_sec.toFixed(1), p.avg_scroll_pct.toFixed(1), (p.bounce_rate * 100).toFixed(2) + "%", p.entry_count, p.exit_count]),
              ]} />
            }>
              <Table
                headers={["Sida", "Visningar", "Unika", "Snitt tid", "Scroll %", "Bounce", "Entries", "Exits"]}
                rows={pages.data.map((p) => [p.path, fmt(p.views), fmt(p.unique_visitors), fmtDur(p.avg_time_sec), p.avg_scroll_pct.toFixed(0) + "%", fmtPct(p.bounce_rate, 0), fmt(p.entry_count), fmt(p.exit_count)])}
              />
            </Section>
          )}
        </Pane>
      )}

      {/* ENGAGEMENT */}
      {tab === "engagement" && (
        <Pane q={engagement}>
          {engagement.data && (
            <>
              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                <Kpi label="Bounce-rate" k={{ value: engagement.data.bounce_rate, prev: 0, delta_pct: null }} format={(n) => fmtPct(n)} />
                <Kpi label="Engaged-rate" k={{ value: engagement.data.engaged_rate, prev: 0, delta_pct: null }} format={(n) => fmtPct(n)} />
                <Kpi label="Snitt sessionstid" k={{ value: engagement.data.avg_session_sec, prev: 0, delta_pct: null }} format={(n) => fmtDur(n)} />
                <Kpi label="Sidor/session" k={{ value: engagement.data.avg_pages_per_session, prev: 0, delta_pct: null }} format={(n) => n.toFixed(2)} />
              </div>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <Section title="Sessionslängd">
                  <SimpleBar data={engagement.data.session_buckets} x="label" y="count" name="Sessioner" />
                </Section>
                <Section title="Scrolldjup (max % per sidvisning)">
                  <SimpleBar data={engagement.data.scroll_buckets} x="label" y="count" name="Sidvisningar" />
                </Section>
              </div>
              <Section title="Events per dag">
                <SimpleLine data={engagement.data.events_by_day} x="date" y="count" name="Events" />
              </Section>
              <Section title="Topp-events" action={
                <ExportBtn name="events" rows={[["Event", "Kategori", "Antal", "Unika sessioner"], ...engagement.data.top_events.map((e) => [e.name, e.category ?? "", e.count, e.unique_sessions])]} />
              }>
                <Table
                  headers={["Event", "Kategori", "Antal", "Unika sessioner"]}
                  rows={engagement.data.top_events.map((e) => [e.name, e.category ?? "—", fmt(e.count), fmt(e.unique_sessions)])}
                />
              </Section>
            </>
          )}
        </Pane>
      )}

      {/* FUNNELS */}
      {tab === "funnels" && (
        <Pane q={funnels}>
          {funnels.data && (
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {funnels.data.map((f) => (
                <Section key={f.id} title={f.label}>
                  <p className="mb-3 text-xs text-white/50">{f.description}</p>
                  <div className="space-y-2">
                    {f.steps.map((s, i) => {
                      const w = f.steps[0]?.visitors ? (s.visitors / f.steps[0].visitors) * 100 : 0;
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between text-xs text-white/70">
                            <span>{i + 1}. {s.label}</span>
                            <span>{fmt(s.visitors)} besökare · {s.conv_from_start_pct.toFixed(1)}%{i > 0 && <span className="ml-2 text-red-400">−{s.drop_off_pct.toFixed(1)}%</span>}</span>
                          </div>
                          <div className="mt-1 h-3 overflow-hidden rounded bg-white/5">
                            <div className="h-full bg-mint" style={{ width: `${Math.max(2, w)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Section>
              ))}
            </div>
          )}
        </Pane>
      )}

      {/* RETENTION */}
      {tab === "retention" && (
        <Pane q={retention}>
          {retention.data && (
            <>
              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3">
                <Kpi label="Nya besökare" k={{ value: retention.data.new_visitors, prev: 0, delta_pct: null }} />
                <Kpi label="Återvändande" k={{ value: retention.data.returning_visitors, prev: 0, delta_pct: null }} />
                <Kpi
                  label="Återvändande-andel"
                  k={{ value: retention.data.returning_visitors / Math.max(1, retention.data.new_visitors + retention.data.returning_visitors), prev: 0, delta_pct: null }}
                  format={(n) => fmtPct(n)}
                />
              </div>
              <Section title="Kohorter (per ISO-vecka)">
                <div className="overflow-x-auto">
                  <table className="text-xs">
                    <thead>
                      <tr className="text-white/50">
                        <th className="px-2 py-1 text-left">Vecka</th>
                        <th className="px-2 py-1 text-left">Storlek</th>
                        {Array.from({ length: 8 }).map((_, i) => (<th key={i} className="px-2 py-1 text-center">+{i + 1}v</th>))}
                      </tr>
                    </thead>
                    <tbody>
                      {retention.data.cohorts.map((c) => (
                        <tr key={c.week}>
                          <td className="px-2 py-1 text-white/80">{c.week}</td>
                          <td className="px-2 py-1 text-white/80">{c.size}</td>
                          {c.week_returns.map((v, i) => {
                            const pct = c.size ? v / c.size : 0;
                            const bg = `rgba(94, 234, 212, ${Math.min(0.85, pct * 1.2 + 0.05)})`;
                            return (
                              <td key={i} className="px-2 py-1 text-center" style={{ background: pct ? bg : "transparent", color: pct > 0.4 ? "#0a0a0a" : "rgba(255,255,255,0.7)" }}>
                                {v ? `${(pct * 100).toFixed(0)}%` : "—"}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            </>
          )}
        </Pane>
      )}

      {/* CONVERSIONS */}
      {tab === "conversions" && (
        <Pane q={conversions}>
          {conversions.data && (
            <Section title={`${conversions.data.length} konverteringar`} action={
              <ExportBtn name="konverteringar" rows={[
                ["Tid", "Typ", "E-post", "Företag", "UTM-källa", "Medium", "Kampanj", "Referrer", "Landningssida", "Enhet", "Land", "First-touch källa", "First-touch kampanj", "First-touch landning"],
                ...conversions.data.map((r) => [r.created_at, r.source_label, r.email, r.company ?? "", r.channel_source ?? "", r.channel_medium ?? "", r.channel_campaign ?? "", r.referrer_host ?? "", r.landing_path ?? "", r.device_type ?? "", r.country ?? "", r.first_touch_source ?? "", r.first_touch_campaign ?? "", r.first_touch_landing ?? ""]),
              ]} />
            }>
              <Table
                headers={["Tid", "Typ", "E-post", "Företag", "Kanal", "Kampanj", "Landning", "Enhet", "Land", "First-touch"]}
                rows={conversions.data.map((r) => [
                  new Date(r.created_at).toLocaleString("sv-SE"),
                  r.source_label,
                  r.email,
                  r.company ?? "—",
                  r.channel_source || r.referrer_host || "(direct)",
                  r.channel_campaign ?? "—",
                  r.landing_path ?? "—",
                  r.device_type ?? "—",
                  r.country ?? "—",
                  r.first_touch_source ?? "—",
                ])}
              />
            </Section>
          )}
        </Pane>
      )}

      {/* DEVICES */}
      {tab === "devices" && (
        <Pane q={devices}>
          {devices.data && (
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <Section title="Enhetstyp"><Table headers={["Typ", "Sessioner"]} rows={devices.data.devices.map((d) => [d.name, fmt(d.count)])} /></Section>
              <Section title="Browsers"><Table headers={["Browser", "Sessioner"]} rows={devices.data.browsers.map((d) => [d.name, fmt(d.count)])} /></Section>
              <Section title="OS"><Table headers={["OS", "Sessioner"]} rows={devices.data.os.map((d) => [d.name, fmt(d.count)])} /></Section>
              <Section title="Länder"><Table headers={["Land", "Sessioner"]} rows={devices.data.countries.map((d) => [d.name, fmt(d.count)])} /></Section>
            </div>
          )}
        </Pane>
      )}
    </div>
  );
}

function Pane({ q, children }: { q: { isLoading: boolean; error: unknown }; children: React.ReactNode }) {
  return (
    <>
      {q.isLoading && <p className="mt-6 text-sm text-white/60">Laddar…</p>}
      {q.error ? <p className="mt-6 text-sm text-red-400">{(q.error as Error).message}</p> : null}
      {children}
    </>
  );
}
